import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import CodeIcon from '@mui/icons-material/Code';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
// import LoginIcon from '@mui/icons-material/Login';
// import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from 'react-oidc-context';
import usePageTracking from './usePageTracking';

// Components
import Sidebar from './components/Sidebar';
import ScrollToTop from './components/ScrollToTop';
import About from './pages/About';
import Experience from './pages/Experience';
import Projects from './pages/Projects';
import Skills from './pages/Skills';
import Education from './pages/Education';
import Documents from './pages/Documents';
import Contact from './pages/Contact';
import Certifications from './pages/Certifications';
import Speaking from './pages/Speaking';
import Auth from './pages/Auth';
import Profile from './pages/Profile';

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00bcd4',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Styled components
const AppContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 240, // Match drawer width
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
  },
}));

// Add a wrapper component for page tracking
function PageTrackingWrapper({ children }: { children: React.ReactNode }) {
  usePageTracking();
  return <>{children}</>;
}

function OidcCallbackHandler() {
  // This component allows react-oidc-context to process the callback and then redirects to /profile
  React.useEffect(() => {
    // Wait a tick to let OIDC context process, then redirect
    const timeout = setTimeout(() => {
      window.location.replace('/profile');
    }, 500);
    return () => clearTimeout(timeout);
  }, []);
  return null; // Optionally, show a spinner here
}

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const auth = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'About Me', icon: <PersonIcon />, path: '/' },
    { text: 'Experience', icon: <WorkIcon />, path: '/experience' },
    { text: 'Projects', icon: <CodeIcon />, path: '/projects' },
    { text: 'Skills', icon: <BuildIcon />, path: '/skills' },
    { text: 'Education', icon: <SchoolIcon />, path: '/education' },
    { text: 'Certifications', icon: <VerifiedIcon />, path: '/certifications' },
    { text: 'Speaking', icon: <RecordVoiceOverIcon />, path: '/speaking' },
    { text: 'Documents', icon: <DescriptionIcon />, path: '/documents' },
    { text: 'Contact', icon: <EmailIcon />, path: '/contact' },
  ];

  // Add authentication menu items
  const authMenuItems = [{
    text: 'Profile',
        icon: <PersonIcon />,
    path: auth.isAuthenticated ? '/profile' : '/auth'
  }];

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Router>
        <PageTrackingWrapper>
          <ScrollToTop />
          <AppContainer>
            <AppBar position="fixed" sx={{ display: { md: 'none' }, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
              <Toolbar>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                  Michael Suliot
                </Typography>
              </Toolbar>
            </AppBar>
            <Sidebar 
              mobileOpen={mobileOpen} 
              handleDrawerToggle={handleDrawerToggle} 
              menuItems={menuItems}
              authMenuItems={authMenuItems}
            />
            <MainContent>
              <Box sx={{ height: { xs: 56, sm: 64, md: 0 } }} />
              <Routes>
                <Route path="/" element={<About />} />
                <Route path="/experience" element={<Experience />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/skills" element={<Skills />} />
                <Route path="/education" element={<Education />} />
                <Route path="/certifications" element={<Certifications />} />
                <Route path="/speaking" element={<Speaking />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<OidcCallbackHandler />} />
                <Route path="/profile" element={<Profile />} />
              </Routes>
            </MainContent>
          </AppContainer>
        </PageTrackingWrapper>
      </Router>
    </ThemeProvider>
  );
}

export default App;
